import { GOAL_CARD_TEXT } from '@anqi/shared'
import type { PlayerId, RevealPayload } from '../protocol-types'
import { seatStyle } from '../components/Board'

const REASON_TEXT: Record<string, string> = {
  goal: '有人做成了自己的目标',
  stonesExhausted: '三人棋子都用完了，按完成度结算',
  turnLimit: '到了回合上限，按完成度结算',
}

/**
 * 摊牌屏。
 *
 * 这一屏是整局的回报：三个人终于看见彼此在藏什么。
 * 没有语音，所以「原来你在做这个」这种恍然大悟只能靠这一屏给。
 * 完成度也一起显示 —— 输的人想知道自己差多少。
 *
 * 「再来一局」是**在原房间**重开，不回进场屏：约齐三个人本身就是最大的成本，
 * 打完一局还要重新念一遍房间码是最让人不想再来一局的地方。
 */
interface RevealScreenProps {
  reveal: RevealPayload
  mySeat: PlayerId
  /** 原房间直接重开。只有房主（0 号座）能发起，服务端也会拦。 */
  onRematch(): void
  /** 退出房间，回进场屏 */
  onLeave(): void
  pending: boolean
  error: string | null
}

export function RevealScreen({
  reveal,
  mySeat,
  onRematch,
  onLeave,
  pending,
  error,
}: RevealScreenProps) {
  const winner = reveal.winner
  const isHost = mySeat === 0
  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 p-5">
      <div className="text-center">
        <p className="text-sm text-slate-400">对局结束</p>
        <p className="mt-1 text-2xl font-bold">
          {winner === null
            ? '平局'
            : winner === mySeat
              ? '你赢了'
              : `${reveal.players.find((p) => p.seat === winner)?.displayName ?? ''} 赢了`}
        </p>
        {reveal.endReason !== null && (
          <p className="mt-1 text-sm text-slate-400">{REASON_TEXT[reveal.endReason] ?? reveal.endReason}</p>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm text-slate-400">三个人藏的是</p>
        {reveal.players.map((p) => {
          const st = seatStyle(p.seat)
          const text = GOAL_CARD_TEXT[p.goal]
          return (
            <div
              key={p.seat}
              className={[
                'rounded-lg border p-3',
                p.seat === winner ? 'border-amber-400 bg-amber-950/30' : 'border-slate-700 bg-slate-800',
              ].join(' ')}
            >
              <div className="flex items-baseline justify-between">
                <span className="flex items-center gap-1 text-sm font-semibold">
                  <span className={st.text}>{st.glyph}</span>
                  {p.displayName}
                  {p.seat === mySeat && <span className="text-xs text-slate-400">（你）</span>}
                </span>
                <span className="text-base font-bold">{text.name}</span>
              </div>
              <p className="mt-1 text-sm text-slate-300">{text.description}</p>
              {p.goal === 'siege' && p.siegeTarget !== null && (
                <p className="mt-1 text-sm text-amber-300">
                  他要围的是 {seatStyle(p.siegeTarget).label}方
                </p>
              )}
              {/* 完成度：输的人最想知道的就是「我差多少」 */}
              <div className="mt-2 flex items-center gap-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-700">
                  <div
                    className="h-full rounded-full bg-amber-400"
                    style={{ width: `${Math.round(p.progress * 100)}%` }}
                  />
                </div>
                <span className="w-10 text-right text-xs text-slate-400">
                  {Math.round(p.progress * 100)}%
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {error !== null && (
        <p role="alert" className="rounded-md bg-red-950/60 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={!isHost || pending}
        onClick={onRematch}
        className="min-h-[52px] rounded-lg bg-amber-500 text-base font-bold text-slate-900 disabled:bg-slate-700 disabled:text-slate-400"
      >
        {isHost ? '再来一局（还是这个房间）' : '等房主开下一局'}
      </button>

      <button
        type="button"
        onClick={onLeave}
        className="min-h-[44px] text-sm text-slate-400 underline"
      >
        退出房间
      </button>
    </div>
  )
}
